import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Progress, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { CODIFICATION_QUESTION_TYPE } from '../../../constant/codification';
import { TH5PLessonConfig, TH5PQuiz } from '../../../models/courses/h5p';
import H5PQuizModal from './H5PQuizModal';

import './H5PLessonPlayer.css';

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeAPIPromise: Promise<void> | null = null;

const loadYouTubeAPI = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }
  if (youtubeAPIPromise) {
    return youtubeAPIPromise;
  }
  youtubeAPIPromise = new Promise((resolve) => {
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(script);
    }
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };
  });
  return youtubeAPIPromise;
};

const extractYoutubeId = (url: string): string | null => {
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.replace('/', '');
    }
    if (parsed.searchParams.has('v')) {
      return parsed.searchParams.get('v');
    }
    if (parsed.pathname.includes('/embed/')) {
      return parsed.pathname.split('/embed/')[1];
    }
    return null;
  } catch (error) {
    return null;
  }
};

const formatTime = (value: number): string => {
    if (!Number.isFinite(value) || value < 0) {
      return '0:00';
    }
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };
  
  interface H5PLessonPlayerProps {
    config: TH5PLessonConfig;
    onComplete: () => void;
  }

  const evaluateQuiz = (quiz: TH5PQuiz, value: string | string[]): boolean => {
    switch (quiz.questionType) {
      case CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE:
        return quiz.answers.some((answer) => answer.id === value && answer.isCorrect);
      case CODIFICATION_QUESTION_TYPE.MULTI_CHOICE: {
        const expected = new Set(quiz.answers.filter((answer) => answer.isCorrect).map((answer) => answer.id));
        const provided = new Set(Array.isArray(value) ? value : []);
        if (expected.size === 0 || expected.size !== provided.size) {
          return false;
        }
        for (const id of expected) {
          if (!provided.has(id)) {
            return false;
          }
        }
        return true;
      }
      case CODIFICATION_QUESTION_TYPE.FILL_IN_THE_BLANKS: {
        if (!Array.isArray(value)) {
          return false;
        }
        const expectedAnswers = quiz.answers;
        if (expectedAnswers.length !== value.length) {
          return false;
        }
        return expectedAnswers.every((answer, index) => {
          const normalizedExpected = (answer.label ?? '').trim().toLowerCase();
          const normalizedValue = (value[index] ?? '').trim().toLowerCase();
          return normalizedExpected === '*' || (normalizedExpected.length > 0 && normalizedExpected === normalizedValue);
        });
      }
      case CODIFICATION_QUESTION_TYPE.DRAG_AND_DROP: {
        if (!Array.isArray(value)) {
          return false;
        }
        const targetAnswers = quiz.answers.filter((answer) => answer.isCorrect);
        if (targetAnswers.length !== value.length) {
          return false;
        }
        return targetAnswers.every((answer, index) => answer.id === value[index]);
      }
      case CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT: {
        if (typeof value !== 'string') return false;
        const target = parseFloat(quiz.answers[0]?.label ?? '');
        const given = parseFloat(value);
        return !Number.isNaN(target) && !Number.isNaN(given) && target === given;
      }
      case CODIFICATION_QUESTION_TYPE.TEXT_INPUT:
      default: {
        if (typeof value !== 'string') return false;
        const expected = (quiz.answers[0]?.label ?? '').trim().toLowerCase();
        return expected.length > 0 && expected === value.trim().toLowerCase();
      }
    }
  };

const H5PLessonPlayer: React.FC<H5PLessonPlayerProps> = ({ config, onComplete }) => {
  const { t } = useTranslation();
  const containerId = useMemo(() => `h5p-player-${Math.random().toString(36).slice(2, 10)}`, []);
  const playerRef = useRef<any>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<TH5PQuiz | null>(null);
  const [triggeredQuizIds, setTriggeredQuizIds] = useState<Set<string>>(new Set());
  const [completedQuizIds, setCompletedQuizIds] = useState<Set<string>>(new Set());
  const [currentTime, setCurrentTime] = useState(0);

  const allowSkipping = config.allowSkipping ?? false;
  const sortedQuizzes = useMemo(
    () => (config.quizzes ?? []).slice().sort((a, b) => a.time - b.time),
    [config.quizzes],
  );

  const videoId = useMemo(() => extractYoutubeId(config.videoUrl), [config.videoUrl]);

  useEffect(() => {
    let isMounted = true;
    if (!videoId) {
      return () => {
        isMounted = false;
      };
    }
    loadYouTubeAPI().then(() => {
      if (!isMounted) {
        return;
      }
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      playerRef.current = new window.YT.Player(containerId, {
        videoId,
        playerVars: {
          controls: allowSkipping ? 1 : 0,
          disablekb: allowSkipping ? 0 : 1,
          modestbranding: 1,
        },
        events: {
          onReady: () => {
            setPlayerReady(true);
            setIsPlaying(true);
          },
          onStateChange: (event: any) => {
            if (!window.YT) return;
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
            }
            if (event.data === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
            }
            if (event.data === window.YT.PlayerState.ENDED) {
              if (sortedQuizzes.length === 0) {
                onComplete();
              }
            }
          },
        },
      });
    });
    return () => {
      isMounted = false;
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      setPlayerReady(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setActiveQuiz(null);
      setTriggeredQuizIds(new Set());
      setCompletedQuizIds(new Set());
    };
  }, [allowSkipping, containerId, onComplete, sortedQuizzes.length, videoId]);

  useEffect(() => {
    if (!playerReady || !playerRef.current) {
      return;
    }
    const interval = window.setInterval(() => {
      if (!playerRef.current || !playerRef.current.getCurrentTime) {
        return;
      }
      const time = playerRef.current.getCurrentTime();
      if (typeof time === 'number') {
        setCurrentTime(time);
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, [playerReady]);

  useEffect(() => {
    if (!playerReady || !playerRef.current) {
      return;
    }
    if (isPlaying && !activeQuiz) {
      playerRef.current.playVideo?.();
    } else {
      playerRef.current.pauseVideo?.();
    }
  }, [activeQuiz, isPlaying, playerReady]);

  useEffect(() => {
    if (activeQuiz || sortedQuizzes.length === 0) {
      return;
    }
    const nextQuiz = sortedQuizzes.find(
      (quiz) => currentTime >= quiz.time && !triggeredQuizIds.has(quiz.id),
    );
    if (nextQuiz) {
      setTriggeredQuizIds((prev) => new Set(prev).add(nextQuiz.id));
      setActiveQuiz(nextQuiz);
      if (nextQuiz.pauseVideo !== false) {
        setIsPlaying(false);
      }
    }
  }, [activeQuiz, currentTime, sortedQuizzes, triggeredQuizIds]);

  useEffect(() => {
    if (!activeQuiz) {
      return;
    }
    const doc = typeof document !== 'undefined' ? (document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void> | void;
      mozFullScreenElement?: Element | null;
      mozCancelFullScreen?: () => Promise<void> | void;
      msFullscreenElement?: Element | null;
      msExitFullscreen?: () => Promise<void> | void;
    }) : null;

    if (!doc) {
      return;
    }

    const exitFullscreen = () => {
      try {
        if (doc.fullscreenElement && doc.exitFullscreen) {
          return doc.exitFullscreen();
        }
        if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) {
          return doc.webkitExitFullscreen();
        }
        if (doc.mozFullScreenElement && doc.mozCancelFullScreen) {
          return doc.mozCancelFullScreen();
        }
        if (doc.msFullscreenElement && doc.msExitFullscreen) {
          return doc.msExitFullscreen();
        }
      } catch (error) {
        // Ignored: exiting fullscreen is best-effort only.
      }
      return undefined;
    };

    const result = exitFullscreen();
    if (result && typeof (result as Promise<void>).catch === 'function') {
      (result as Promise<void>).catch(() => undefined);
    }
  }, [activeQuiz]);

  useEffect(() => {
    if (sortedQuizzes.length > 0 && completedQuizIds.size === sortedQuizzes.length) {
      onComplete();
    }
  }, [completedQuizIds, onComplete, sortedQuizzes.length]);

  const handleQuizAnswer = (value: string | string[]) => {
    if (!activeQuiz) return;
    const success = evaluateQuiz(activeQuiz, value);
    if (success) {
      message.success(activeQuiz.feedbackCorrect || t('h5p.player.messages.defaultSuccess'));
      setCompletedQuizIds((prev) => new Set(prev).add(activeQuiz.id));
      setActiveQuiz(null);
      setIsPlaying(true);
    } else {
      message.warning(activeQuiz.feedbackIncorrect || t('h5p.player.messages.defaultError'));
      if (activeQuiz.allowRetry === false) {
        setCompletedQuizIds((prev) => new Set(prev).add(activeQuiz.id));
        setActiveQuiz(null);
        setIsPlaying(true);
      }
    }
  };

  const handleQuizCancel = () => {
    if (!activeQuiz) {
      return;
    }
    setActiveQuiz(null);
    setIsPlaying(true);
  };

  if (!videoId) {
    return (
      <Alert type="warning" showIcon message={t('h5p.player.messages.invalidUrl')} />
    );
  }

  const totalQuizzes = sortedQuizzes.length;
  const completedCount = completedQuizIds.size;
  const progressPercent = totalQuizzes > 0 ? Math.round((completedCount / totalQuizzes) * 100) : 0;

  return (
    <Card className="h5p-player-card" bordered={false}>
      <div className="h5p-player-header">
        <div>
          <Typography.Title level={4} className="h5p-player-title">
            {t('h5p.player.card.title')}
          </Typography.Title>
          <Typography.Paragraph className="h5p-player-subtitle">
            {t('h5p.player.card.subtitle')}
          </Typography.Paragraph>
        </div>
        {totalQuizzes > 0 && (
          <div className="h5p-player-progress">
            <Typography.Text strong>
              {t('h5p.player.progressLabel', {
                completed: completedCount,
                total: totalQuizzes,
                count: totalQuizzes,
              })}
            </Typography.Text>
            <Progress percent={progressPercent} showInfo={false} strokeColor="#3f87ff" size="small" />
          </div>
        )}
      </div>
      <div className="h5p-player-video-wrapper">
        <div className="h5p-player-video-inner">
          <div id={containerId} className="h5p-player-video" />
        </div>
        {!playerReady && (
          <div className="h5p-player-video-overlay">
            <Spin tip={t('h5p.player.loadingVideo')} size="large" />
          </div>
        )}
      </div>
      {!allowSkipping && (
        <Alert
          className="h5p-player-alert"
          type="info"
          showIcon
          message={t('h5p.player.messages.manualProgressDisabled')}
        />
      )}
      {totalQuizzes > 0 && (
        <div className="h5p-player-quiz-timeline">
          <Typography.Text type="secondary">{t('h5p.player.timelineTitle')}</Typography.Text>
          <div className="h5p-player-quiz-tags">
            {sortedQuizzes.map((quiz, index) => {
              const status = completedQuizIds.has(quiz.id)
                ? 'done'
                : triggeredQuizIds.has(quiz.id)
                ? 'active'
                : 'upcoming';
              const label = quiz.title?.trim() || t('h5p.player.activityLabel', { index: index + 1 });
              return (
                <Tooltip
                  key={quiz.id}
                  title={
                    <div className="h5p-player-quiz-tooltip">
                      <span className="h5p-player-quiz-tooltip-title">{label}</span>
                      <span className="h5p-player-quiz-tooltip-question">{quiz.question}</span>
                      <span className="h5p-player-quiz-tooltip-time">
                        {t('h5p.player.tooltipTime', { time: formatTime(quiz.time) })}
                      </span>
                    </div>
                  }
                >
                  <Tag className={`h5p-player-quiz-tag h5p-player-quiz-tag--${status}`}>
                    {t('h5p.player.quizTag', { time: formatTime(quiz.time), label })}
                  </Tag>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}
      <div className="h5p-player-actions">
        <Button
          type="primary"
          size="large"
          disabled={!playerReady}
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={() => setIsPlaying((prev) => !prev)}
        >
          {isPlaying ? t('h5p.player.actions.pause') : t('h5p.player.actions.resume')}
        </Button>
        <Typography.Text type="secondary">
          {playerReady
            ? t('h5p.player.currentTime', { time: formatTime(currentTime) })
            : t('h5p.player.initializing')}
        </Typography.Text>
      </div>
      <H5PQuizModal
        open={!!activeQuiz}
        quiz={activeQuiz}
        onSubmit={handleQuizAnswer}
        onCancel={handleQuizCancel}
        allowSkip={allowSkipping}
      />
    </Card>
  );
};

export default H5PLessonPlayer;